'use client';

import React, { useState, useEffect, useMemo, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  BookOpen, 
  Map as MapIcon, 
  Swords, 
  LayoutDashboard, 
  Zap, 
  Brain, 
  Target,
  Lock,
  FileText,
  Library,
  Smartphone,
  Monitor,
  Tablet,
  RefreshCw,
  ArrowLeft,
  Loader2,
  ChevronRight,
  ChevronDown,
  Beaker,
  Type,
  Trash2,
  Trophy,
} from 'lucide-react';
import { Card } from '@/components/ui/card';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import Link from 'next/link';
import { useRouter } from 'next/navigation';
import { fetchLessonContent } from './actions';
import {
  getGamificationDashboardState,
  getMapState,
  resetFounderLessonGamificationAction,
  setFounderTierBypassAction
} from '@/app/actions/gamification';
import LessonEngine from './LessonEngine';
import GamifiedTree, { GamificationState } from '@/components/gamification/gamified-tree';
import NextLessonCard from '@/components/gamification/next-lesson-card';
import { RoleFilterModal } from '@/components/gamification/role-filter-modal';
import { CHARACTER_ROLE_TYPES } from '@/types/enums';
import { normalizeRoles } from '@/integrity/iam/permissions';
import GamifyLabClient from './GamifyLabClient';
import JungleAcademyClient from './JungleAcademyClient';
import { CharacterProgressionModal } from './CharacterProgressionModal';
import { LearningAppHeader, LearningAppHeaderButton, LearningAppReaderControls, LearningAppSegmentTabs, LearningAppTierControls } from './LearningAppHeader';
import type { LessonTierVariant } from '@/lib/gamification/contracts/lesson-variants';
import type { LessonTierState } from '@/lib/gamification/services/lesson-tier-state';

export interface LessonNode {
  id: string;
  name: string;
  title: string;
  path: string;
  sha?: string;
  type: 'dir' | 'file';
  roleGate?: string;
  badge?: string;
  isPersonal?: boolean;
  isBaseLesson?: boolean;
  children: LessonNode[];
  userHasRole?: boolean;
}

interface ClassesClientViewProps {
  userRoles: string[];
  userName?: string;
  character?: any; // The full Character entity
  isAuthenticated?: boolean;
  lessonTree: LessonNode[];
  ebooksTree: LessonNode[];
}

type TabState = 'lessons' | 'map' | 'challenges' | 'ebooks' | 'dash' | 'lab' | 'jungle-academy';
type FilterState = 'not-started' | 'in-progress' | 'completed';
type DevView = 'desktop' | 'mobile' | 'landscape';

const TreeNode = ({ node, userRoles, depth, onFileClick, bypassLocked }: { node: LessonNode, userRoles: string[], depth: number, onFileClick?: (file: LessonNode) => void, bypassLocked?: boolean }) => {
  const [isExpanded, setIsExpanded] = useState(() => {
    if (typeof window !== 'undefined') {
      try {
        const saved = localStorage.getItem(`ebooks-tree-expanded-${node.id}`);
        if (saved !== null) return JSON.parse(saved);
      } catch (e) {}
    }
    return depth === 0; // Root is expanded by default
  });

  useEffect(() => {
    localStorage.setItem(`ebooks-tree-expanded-${node.id}`, JSON.stringify(isExpanded));
  }, [isExpanded, node.id]);

  const isLocked = !bypassLocked && node.roleGate && !userRoles.includes(node.roleGate);

  if (isLocked) {
    return (
      <div className="mb-2 opacity-50 select-none">
        <div className="flex items-center gap-2 p-2">
          <Lock className="w-4 h-4 text-muted-foreground" />
          <h2 className="text-xs font-bold uppercase tracking-wider text-muted-foreground">
            {node.title} (Requires {node.roleGate})
          </h2>
        </div>
      </div>
    );
  }

  if (node.type === 'file') {
    return (
      <Card onClick={() => onFileClick?.(node)} className="p-3 flex items-center gap-4 relative z-10 border-primary/20 hover:border-primary/50 shadow-sm cursor-pointer group transition-colors">
        <div className="w-10 h-10 rounded-full bg-primary/10 border border-primary/30 flex items-center justify-center shrink-0 group-hover:bg-primary/20 transition-colors">
          <FileText className="w-5 h-5 text-primary" />
        </div>
        <div className="flex-1 min-w-0">
          <h3 className="font-medium text-sm truncate group-hover:text-primary transition-colors capitalize flex items-center gap-2">
            {node.title}
            {node.badge && <span className="px-1.5 py-0.5 text-[10px] bg-primary/10 text-primary border border-primary/20 rounded uppercase tracking-wider">{node.badge}</span>}
          </h3>
          <p className="text-xs text-muted-foreground truncate">Interactive Document</p>
        </div>
      </Card>
    );
  }

  // Sort children: directories first, then files. Alphabetically.
  const sortedChildren = [...node.children].sort((a, b) => {
    if (a.type !== b.type) {
      return a.type === 'file' ? -1 : 1;
    }
    return a.title.localeCompare(b.title);
  });

  return (
    <div className="mb-2">
      <button
        type="button"
        onClick={() => setIsExpanded(!isExpanded)}
        className="flex items-center gap-2 p-2 w-full hover:bg-muted/50 rounded-lg transition-colors group"
      >
        {isExpanded ? <ChevronDown className="w-4 h-4 text-primary" /> : <ChevronRight className="w-4 h-4 text-muted-foreground group-hover:text-primary" />}
        <h2 className="text-xs font-bold uppercase tracking-wider text-primary/80">
          {node.title}
        </h2>
      </button>
      
      <AnimatePresence>
        {isExpanded && (
          <motion.div 
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="overflow-hidden"
          >
            <div className={`flex flex-col gap-2 ${depth > 0 ? 'ml-4 mt-2 border-l border-border/30 pl-4' : 'mt-2'}`}>
              {sortedChildren.map((child) => (
                <TreeNode key={child.path} node={child} userRoles={userRoles} depth={depth + 1} onFileClick={onFileClick} bypassLocked={bypassLocked} />
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

const RecursiveTree = ({ nodes, userRoles, depth = 0, onFileClick, bypassLocked }: { nodes: LessonNode[], userRoles: string[], depth?: number, onFileClick?: (file: LessonNode) => void, bypassLocked?: boolean }) => {
  // Sort root nodes
  const sortedNodes = [...nodes].sort((a, b) => {
    if (a.type !== b.type) return a.type === 'file' ? -1 : 1;
    return a.title.localeCompare(b.title);
  });

  return (
    <div className={`flex flex-col gap-2 ${depth > 0 ? 'ml-4 mt-2 border-l border-border/30 pl-4' : ''}`}>
      {sortedNodes.map((node) => (
        <TreeNode key={node.path} node={node} userRoles={userRoles} depth={depth} onFileClick={onFileClick} bypassLocked={bypassLocked} />
      ))}
    </div>
  );
};

export default function ClassesClientView({ userRoles, userName = 'User', character, isAuthenticated = false, lessonTree, ebooksTree }: ClassesClientViewProps) {
  const [localLessonTree, setLocalLessonTree] = useState<LessonNode[]>([]);
  const [localEbooksTree, setLocalEbooksTree] = useState<LessonNode[]>([]);
  const [offlineUserRoles, setOfflineUserRoles] = useState<string[]>([]);
  const [isOnline, setIsOnline] = useState(true);

  useEffect(() => {
    const updateOnlineState = () => setIsOnline(navigator.onLine);
    updateOnlineState();
    window.addEventListener('online', updateOnlineState);
    window.addEventListener('offline', updateOnlineState);
    return () => {
      window.removeEventListener('online', updateOnlineState);
      window.removeEventListener('offline', updateOnlineState);
    };
  }, []);

  // Keep only the identity data needed to render the cached library. This is
  // not an authentication token and cannot be used to call protected APIs.
  useEffect(() => {
    try {
      const saved = localStorage.getItem('classes-offline-profile');
      if (saved) {
        const profile = JSON.parse(saved) as { roles?: unknown };
        if (Array.isArray(profile.roles)) {
          setOfflineUserRoles(profile.roles.filter((role): role is string => typeof role === 'string'));
        }
      }

      if (userRoles.length > 0) {
        localStorage.setItem('classes-offline-profile', JSON.stringify({
          roles: userRoles,
          userName,
          savedAt: new Date().toISOString(),
        }));
        setOfflineUserRoles(userRoles);
      }
    } catch (error) {
      console.warn('Failed to restore offline Classes profile:', error);
    }
  }, [userRoles, userName]);

  const effectiveUserRoles = userRoles.length > 0 ? userRoles : offlineUserRoles;

  useEffect(() => {
    if (lessonTree && lessonTree.length > 0) {
      setLocalLessonTree(lessonTree);
      try {
        localStorage.setItem('cached_lesson_tree', JSON.stringify(lessonTree));
      } catch (e) {
        console.warn('Failed to cache lesson tree:', e);
      }
    } else {
      try {
        const cached = localStorage.getItem('cached_lesson_tree');
        if (cached) {
          setLocalLessonTree(JSON.parse(cached));
        }
      } catch (e) {
        console.warn('Failed to load cached lesson tree:', e);
      }
    }
  }, [lessonTree]);

  useEffect(() => {
    if (ebooksTree && ebooksTree.length > 0) {
      setLocalEbooksTree(ebooksTree);
      try {
        localStorage.setItem('cached_ebooks_tree', JSON.stringify(ebooksTree));
      } catch (e) {
        console.warn('Failed to cache ebooks tree:', e);
      }
    } else {
      try {
        const cached = localStorage.getItem('cached_ebooks_tree');
        if (cached) {
          setLocalEbooksTree(JSON.parse(cached));
        }
      } catch (e) {
        console.warn('Failed to load cached ebooks tree:', e);
      }
    }
  }, [ebooksTree]);

  const normalizedUserRoles = useMemo(() => normalizeRoles(effectiveUserRoles), [effectiveUserRoles]);
  const defaultVisibleRoles = useMemo(() => {
    const regularRoles = CHARACTER_ROLE_TYPES.REGULAR.map(r => r.toLowerCase());
    const initialVisible = normalizedUserRoles.filter(r => regularRoles.includes(r));
    return initialVisible;
  }, [normalizedUserRoles]);
  const [showSplash, setShowSplash] = useState(true);
  const [activeTab, setActiveTab] = useState<TabState>('lessons');
  const [activeFilter, setActiveFilter] = useState<FilterState>('not-started');
  const [showProgressionModal, setShowProgressionModal] = useState(false);
  
  // Reader View State
  const [selectedFile, setSelectedFile] = useState<LessonNode | null>(null);
  const [selectedFileTab, setSelectedFileTab] = useState<TabState | null>(null);
  const [isSavedOffline, setIsSavedOffline] = useState(false);

  // Role Filtering State
  const [visibleRoles, setVisibleRoles] = useState<string[]>(defaultVisibleRoles);
  
  const [showLockedRoles, setShowLockedRoles] = useState(false);
  const [showBaseLessons, setShowBaseLessons] = useState(true);
  
  const [isPWA, setIsPWA] = useState(false);

  useEffect(() => {
    setVisibleRoles(defaultVisibleRoles);
  }, [defaultVisibleRoles]);

  useEffect(() => {
    try {
      if (typeof window !== 'undefined') {
        const isStandalone = window.matchMedia('(display-mode: standalone)').matches || (window.navigator as any).standalone === true;
        setIsPWA(isStandalone);
      }
      
      const savedVisibleRoles = localStorage.getItem('classes-visibleRoles');
      if (savedVisibleRoles) {
        setVisibleRoles(JSON.parse(savedVisibleRoles));
      }

      const savedShowLockedRoles = localStorage.getItem('classes-showLockedRoles');
      if (savedShowLockedRoles) {
        setShowLockedRoles(JSON.parse(savedShowLockedRoles));
      }
    } catch {}
  }, []);

  useEffect(() => {
    localStorage.setItem('classes-visibleRoles', JSON.stringify(visibleRoles));
  }, [visibleRoles]);

  useEffect(() => {
    localStorage.setItem('classes-showLockedRoles', JSON.stringify(showLockedRoles));
  }, [showLockedRoles]);
  const [fileContent, setFileContent] = useState<string | null>(null);
  const [isLoadingContent, setIsLoadingContent] = useState(false);
  const [contentError, setContentError] = useState<string | null>(null);
  const [focusedDir, setFocusedDir] = useState<LessonNode | null>(null);
  const [fontSizeLevel, setFontSizeLevel] = useState(1); // 0=small, 1=base, 2=large, 3=xl
  const [fontFamily, setFontFamily] = useState<string>('inter');
  const [fullWidth, setFullWidth] = useState(false);
  const [readerSelectedTier, setReaderSelectedTier] = useState<LessonTierVariant | null>(null);
  const [readerTierStates, setReaderTierStates] = useState<LessonTierState[]>([]);
  const [isLocalhost, setIsLocalhost] = useState(false);
  const [activeLessonAreaId, setActiveLessonAreaId] = useState<string | null>(null);

  useEffect(() => {
    const saved = localStorage.getItem('lesson-font-family');
    if (saved) setFontFamily(saved);
  }, []);

  useEffect(() => {
    localStorage.setItem('lesson-font-family', fontFamily);
  }, [fontFamily]);

  // Auto-cache is opt-in, and only caches content the learner actually opens.
  // We must not walk the whole library in the background: every file would
  // create an authenticated Server Action request and can overwhelm the app.
  const [autoCacheEnabled, setAutoCacheEnabled] = useState(true);
  useEffect(() => {
    const saved = localStorage.getItem('classes-auto-cache');
    if (saved) setAutoCacheEnabled(JSON.parse(saved));
  }, []);
  useEffect(() => {
    localStorage.setItem('classes-auto-cache', JSON.stringify(autoCacheEnabled));
  }, [autoCacheEnabled]);

  const [showChatAgent, setShowChatAgent] = useState(true);
  useEffect(() => {
    const saved = localStorage.getItem('show-chat-agent');
    if (saved) setShowChatAgent(saved !== 'false');
  }, []);
  
  const handleChatAgentToggle = (checked: boolean) => {
    setShowChatAgent(checked);
    localStorage.setItem('show-chat-agent', checked.toString());
    // Dispatch event so FeedbackChat updates immediately without reload
    window.dispatchEvent(new CustomEvent('chatAgentVisibilityChanged', { detail: { visible: checked } }));
  };

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const hostname = window.location.hostname.toLowerCase();
    setIsLocalhost(hostname === 'localhost' || hostname === '127.0.0.1' || hostname === '::1');
  }, []);

  // On-demand revalidation state
  const [isRefreshing, setIsRefreshing] = useState(false);
  const router = useRouter();

  // Map View State
  const [gamificationState, setGamificationState] = useState<GamificationState>({});
  const [isMapLoading, setIsMapLoading] = useState(true);
  const [dashboardState, setDashboardState] = useState<any>(null);
  const [isDashboardLoading, setIsDashboardLoading] = useState(false);
  const [isDashboardMutating, setIsDashboardMutating] = useState(false);
  const [confirmClearCache, setConfirmClearCache] = useState(false);

  const fetchMapData = async () => {
    const slugs: string[] = [];
    const extractSlugs = (nodes: LessonNode[]) => {
      if (!nodes) return;
      for (const node of nodes) {
        if (node.type === 'file') {
          slugs.push(node.id.replace('.md', ''));
        } else {
          extractSlugs(node.children || []);
        }
      }
    };
    extractSlugs(localLessonTree);

    try {
      const res = await getMapState(slugs);
      if (res?.success && res.nodeStates) {
        setGamificationState(res.nodeStates as GamificationState);
      }
    } catch (e) {
      console.warn("Offline: Could not load gamification map state.");
    } finally {
      setIsMapLoading(false);
    }
  };

  useEffect(() => {
    fetchMapData();
  }, [localLessonTree]);

  const fetchDashboard = async () => {
    setIsDashboardLoading(true);
    try {
      const res = await getGamificationDashboardState();
      if (res?.success && res.dashboard) {
        setDashboardState(res.dashboard);
        if (res.dashboard.founderBypassActive) {
          const allRoles = CHARACTER_ROLE_TYPES.REGULAR.map(r => r.toLowerCase());
          setVisibleRoles(allRoles);
        }
      }
    } catch (e) {
      console.warn("Offline: Could not load dashboard state.");
    } finally {
      setIsDashboardLoading(false);
    }
  };

  useEffect(() => {
    fetchDashboard();
  }, []);

  useEffect(() => {
    const handleMechanicSolved = async () => {
      await fetchMapData();
      await fetchDashboard();
    };
    window.addEventListener('classes:lesson-mechanic-solved', handleMechanicSolved);
    return () => window.removeEventListener('classes:lesson-mechanic-solved', handleMechanicSolved);
  }, [lessonTree]);

  const handleRefresh = () => {
    setIsRefreshing(true);
    router.replace('/classes?revalidate=true');
    setTimeout(() => {
      router.replace('/classes');
      setIsRefreshing(false);
    }, 1500);
  };

  const handleClearCache = () => {
    const keysToRemove: string[] = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && key.startsWith('offline_lesson_')) {
        keysToRemove.push(key);
      }
    }
    keysToRemove.forEach(k => localStorage.removeItem(k));
    if (selectedFile) {
      setIsSavedOffline(false);
    }
    setConfirmClearCache(false);
  };

  const getCacheKey = (file: LessonNode) => `offline_lesson_${file.id}_${file.sha || 'nohash'}`;

  const cleanupOldCaches = (fileId: string, currentSha: string = 'nohash') => {
    const targetKey = `offline_lesson_${fileId}_${currentSha}`;
    const prefix = `offline_lesson_${fileId}_`;
    const keysToRemove: string[] = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && key.startsWith(prefix) && key !== targetKey) {
        keysToRemove.push(key);
      }
    }
    keysToRemove.forEach(k => localStorage.removeItem(k));
  };

  const handleFileClick = async (file: LessonNode) => {
    setSelectedFile(file);
    setSelectedFileTab(activeTab);
    setIsLoadingContent(true);
    setContentError(null);
    setFileContent(null);
    setReaderSelectedTier(null);
    setReaderTierStates([]);
    setIsSavedOffline(false);
    
    // Check if we already have THIS specific hash saved offline
    const cacheKey = getCacheKey(file);
    const cachedContent = localStorage.getItem(cacheKey);
    if (cachedContent) {
      setIsSavedOffline(true);
    }

    // Cache-first reading is important for the PWA. It avoids an unnecessary
    // authenticated server action and keeps already-downloaded content usable
    // when the session has expired or the device is offline.
    if (cachedContent) {
      setFileContent(cachedContent);
      setIsLoadingContent(false);
      return;
    }

    try {
      const result = await fetchLessonContent(file.path);
      if (result.success && result.content) {
        setFileContent(result.content);
        
        // Auto-cache if enabled
        if (autoCacheEnabled) {
          cleanupOldCaches(file.id, file.sha || 'nohash');
          localStorage.setItem(cacheKey, result.content);
          setIsSavedOffline(true);
        }
      } else {
        // Fallback to offline cache if fetch fails (e.g., no internet)
        if (cachedContent) {
          setFileContent(cachedContent);
        } else {
          setContentError(result.error || 'Failed to load content. You might be offline.');
        }
      }
    } catch (e) {
      if (cachedContent) {
        setFileContent(cachedContent);
      } else {
        setContentError('Network error. You are offline and this lesson was not saved.');
      }
    }
    setIsLoadingContent(false);
  };

  const handleSaveOffline = () => {
    if (selectedFile && fileContent) {
      cleanupOldCaches(selectedFile.id, selectedFile.sha || 'nohash');
      localStorage.setItem(getCacheKey(selectedFile), fileContent);
      setIsSavedOffline(true);
    }
  };

  const handleDownloadMarkdown = () => {
    if (selectedFile && fileContent) {
      const blob = new Blob([fileContent], { type: 'text/markdown;charset=utf-8' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = selectedFile.id;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    }
  };

  // Debug Toggle for Founder
  const [devView, setDevView] = useState<DevView>('desktop');
  const isFounder = effectiveUserRoles.some(role => role?.toLowerCase() === 'founder');

  useEffect(() => {
    const timer = setTimeout(() => setShowSplash(false), 2000);
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    const handleCycleDevView = () => {
      setDevView((prev) => {
        if (prev === 'desktop') return 'mobile';
        if (prev === 'mobile') return 'landscape';
        return 'desktop';
      });
    };
    
    window.addEventListener('cycleDevView', handleCycleDevView);
    return () => window.removeEventListener('cycleDevView', handleCycleDevView);
  }, []);

  const isDesktopLayout = devView === 'desktop' || devView === 'landscape';

  const handleFounderTierBypassToggle = async () => {
    setIsDashboardMutating(true);
    const nextValue = !dashboardState?.founderBypassActive;
    const res = await setFounderTierBypassAction(nextValue);
    if (res.success && 'dashboard' in res && res.dashboard) {
      setDashboardState(res.dashboard);
      if (nextValue) {
        const allRoles = CHARACTER_ROLE_TYPES.REGULAR.map(r => r.toLowerCase());
        setVisibleRoles(allRoles);
      } else {
        setVisibleRoles(defaultVisibleRoles);
      }
      await fetchMapData();
    }
    setIsDashboardMutating(false);
  };

  const handleFounderReset = async () => {
    setIsDashboardMutating(true);
    const res = await resetFounderLessonGamificationAction();
    if (res.success && 'dashboard' in res && res.dashboard) {
      setDashboardState(res.dashboard);
      await fetchMapData();
    }
    setIsDashboardMutating(false);
  };

  const filterTree = (nodes: LessonNode[], parentLocked: boolean = false): LessonNode[] => {
    const bypassLocked = !!dashboardState?.founderBypassActive;
    return nodes
      .map(node => {
        // Personal is content ownership, not an IAM role. It is only
        // available to the Founder and is never a selectable role.
        if (node.isPersonal) {
          if (!normalizedUserRoles.includes('founder') || parentLocked) return null;
          if (node.type === 'file') return { ...node, userHasRole: true };

          const personalChildren = filterTree(node.children, false);
          return personalChildren.length > 0
            ? { ...node, children: personalChildren, userHasRole: true }
            : null;
        }

        if (node.isBaseLesson) {
          if (!showBaseLessons) return null;
          return node.type === 'file' ? { ...node, userHasRole: !parentLocked } : node;
        }

        // Apply roleGate check to BOTH files and directories if they have one!
        const roleGate = node.roleGate ? node.roleGate.toLowerCase() : null;
        let userHasRole = !parentLocked; // If parent is locked, we start out as locked
        
        if (roleGate) {
           const hasSpecificRole = bypassLocked || normalizedUserRoles.includes(roleGate);
           userHasRole = parentLocked ? false : hasSpecificRole;
           
           const roleIsToggledOn = visibleRoles.includes(roleGate);

           if (hasSpecificRole) {
              if (!roleIsToggledOn) return null; // Toggled off, hide it (and its children if it's a dir)
           } else {
              if (!showLockedRoles) return null; // Don't show locked roles (and its children)
           }
        }

        const augmentedNode: LessonNode = { ...node, userHasRole };

        if (node.type === 'file') {
           return augmentedNode; 
        } else {
           const isEffectivelyLocked = augmentedNode.userHasRole === false;
           const filteredChildren = filterTree(node.children, isEffectivelyLocked);
           if (filteredChildren.length > 0) {
              return { ...augmentedNode, children: filteredChildren };
           }
           // If a structural folder had no gated children or they were all filtered out
           return null;
        }
      })
      .filter((node): node is LessonNode => node !== null);
  };

  const filterTreeByProgress = (nodes: LessonNode[]): LessonNode[] => {
    const matchesFilter = (node: LessonNode) => {
      const slug = node.id.replace('.md', '');
      const progressState = gamificationState[slug]?.progressState || 'not-started';
      return progressState === activeFilter;
    };

    return nodes
      .map((node) => {
        if (node.type === 'file') {
          return matchesFilter(node) ? node : null;
        }

        const filteredChildren = filterTreeByProgress(node.children);
        if (filteredChildren.length === 0) {
          return null;
        }

        return {
          ...node,
          children: filteredChildren,
        };
      })
      .filter((node): node is LessonNode => node !== null);
  };

  const roleFilteredLessonTree = useMemo(
    () => filterTree(localLessonTree),
    [localLessonTree, effectiveUserRoles, visibleRoles, showLockedRoles, showBaseLessons, dashboardState?.founderBypassActive]
  );

  const filteredLessonTree = useMemo(
    () => filterTreeByProgress(roleFilteredLessonTree),
    [roleFilteredLessonTree, gamificationState, activeFilter]
  );

  const roleFilteredEbooksTree = useMemo(
    () => filterTree(localEbooksTree),
    [localEbooksTree, effectiveUserRoles, visibleRoles, showLockedRoles, showBaseLessons, dashboardState?.founderBypassActive]
  );

  const lessonAreaItems = useMemo(
    () => filteredLessonTree
      .slice()
      .sort((left, right) => left.title.localeCompare(right.title))
      .map((node) => ({
        id: node.id,
        label: node.title,
        locked: node.userHasRole === false,
      })),
    [filteredLessonTree]
  );

  useEffect(() => {
    if (lessonAreaItems.length === 0) {
      setActiveLessonAreaId(null);
      return;
    }

    const hasActiveArea = activeLessonAreaId
      ? lessonAreaItems.some((item) => item.id === activeLessonAreaId)
      : false;

    if (!hasActiveArea) {
      setActiveLessonAreaId(lessonAreaItems[0].id);
    }
  }, [lessonAreaItems, activeLessonAreaId]);

  useEffect(() => {
    if (!focusedDir) {
      return;
    }

    const containsNode = (nodes: LessonNode[]): boolean => {
      for (const node of nodes) {
        if (node.id === focusedDir.id || node.path === focusedDir.path) {
          return true;
        }
        if (node.type === 'dir' && containsNode(node.children)) {
          return true;
        }
      }
      return false;
    };

    const focusStillVisible = containsNode(filteredLessonTree);

    if (!focusStillVisible) {
      setFocusedDir(null);
    }
  }, [filteredLessonTree, focusedDir]);

  const getContainerClasses = () => {
    if (devView === 'mobile') return 'max-w-md mx-auto h-full border-x border-border/50 shadow-2xl flex-col';
    if (devView === 'landscape') return 'max-w-4xl h-[450px] mx-auto border border-border/50 shadow-2xl mt-10 rounded-xl flex-row overflow-hidden';
    return 'w-full h-full flex flex-col md:flex-row';
  };

  const renderContent = () => {
    if (selectedFile && selectedFileTab === activeTab) {
      return (
        <div className="flex flex-col h-full w-full bg-background absolute inset-0 z-50 overflow-hidden animate-in fade-in zoom-in-95 duration-200">
          <LearningAppHeader
            title={selectedFile.title}
            badgeLabel={selectedFile.badge}
            leading={(
              <LearningAppHeaderButton
                onClick={() => {
                  setSelectedFile(null);
                  setSelectedFileTab(null);
                }}
                title="Back to lesson browser"
                className="w-7 px-0"
              >
                <ArrowLeft className="w-3.5 h-3.5" />
              </LearningAppHeaderButton>
            )}
            titleAside={(
              <LearningAppTierControls
                selectedTier={readerSelectedTier}
                tierStates={readerTierStates}
                onSelectTier={setReaderSelectedTier}
              />
            )}
            actions={(
              <LearningAppReaderControls
                onDecreaseFont={() => setFontSizeLevel(prev => Math.max(0, prev - 1))}
                onIncreaseFont={() => setFontSizeLevel(prev => Math.min(3, prev + 1))}
                onToggleWidth={() => setFullWidth(prev => !prev)}
                fullWidth={fullWidth}
                onSaveOffline={handleSaveOffline}
                isSavedOffline={isSavedOffline}
                onDownload={handleDownloadMarkdown}
              />
            )}
          />
          
          {/* Reader Content Area */}
          <ScrollArea className="flex-1 p-3 sm:p-6 md:p-10 bg-background/95 backdrop-blur">
            <div className={`${fullWidth ? 'max-w-none' : 'max-w-3xl'} mx-auto w-full pb-20`}>
              {isLoadingContent ? (
                <div className="flex flex-col items-center justify-center py-20 opacity-50">
                  <Loader2 className="w-10 h-10 animate-spin text-primary mb-4" />
                  <p>Loading document...</p>
                </div>
              ) : contentError ? (
                <div className="p-4 border border-destructive/50 bg-destructive/10 text-destructive rounded-lg">
                  {contentError}
                </div>
              ) : fileContent && selectedFile ? (
                <LessonEngine 
                  content={fileContent} 
                  fontSizeLevel={fontSizeLevel}
                  fontFamily={fontFamily}
                  lessonSlug={selectedFile.id.replace('.md', '')}
                  selectedTier={readerSelectedTier}
                  onSelectedTierChange={setReaderSelectedTier}
                  onTierStateChange={({ selectedTier, tierStates }) => {
                    setReaderSelectedTier(selectedTier);
                    setReaderTierStates(tierStates);
                  }}
                  showTierControlsInBody={false}
                />
              ) : null}
            </div>
          </ScrollArea>
        </div>
      );
    }

    if (activeTab === 'lessons') {
      if (isMapLoading) {
        return (
          <div className="flex flex-col items-center justify-center py-20 opacity-50">
            <Loader2 className="w-10 h-10 animate-spin text-primary mb-4" />
            <p className="text-sm font-medium">Loading Lessons...</p>
          </div>
        );
      }
      return (
        <div className="flex flex-col gap-6">
          {localLessonTree.length === 0 ? (
            <p className="text-sm text-muted-foreground italic text-center mt-10">No lessons found in the library.</p>
          ) : (
            <GamifiedTree 
              lessonTree={filteredLessonTree}
              gamificationState={gamificationState} 
              onNodeClick={handleFileClick} 
              focusedNode={focusedDir}
              onFocusSelect={setFocusedDir}
              activeParentId={activeLessonAreaId}
              onActiveParentChange={setActiveLessonAreaId}
              showParentTabs={false}
            />
          )}
        </div>
      );
    }
    
    if (activeTab === 'ebooks') {
      return roleFilteredEbooksTree.length === 0 ? (
        <p className="text-sm text-muted-foreground italic text-center mt-10">No Ebooks found in the library.</p>
      ) : (
        <RecursiveTree nodes={roleFilteredEbooksTree} userRoles={effectiveUserRoles} onFileClick={handleFileClick} bypassLocked={dashboardState?.founderBypassActive} />
      );
    }

    if (activeTab === 'map') {
      return (
        <div className="flex flex-col items-center justify-center py-20 opacity-50">
          <MapIcon className="w-16 h-16 text-muted-foreground mb-4" />
          <p className="text-sm text-muted-foreground font-medium">Map View Coming Soon.</p>
        </div>
      );
    }

    if (activeTab === 'challenges') {
      return (
        <div className="flex flex-col items-center justify-center py-20 opacity-50">
          <Swords className="w-16 h-16 text-muted-foreground mb-4" />
          <p className="text-sm text-muted-foreground font-medium">Challenges are currently locked.</p>
        </div>
      );
    }

    if (activeTab === 'dash') {
      return (
        <div className="flex flex-col items-center justify-center py-20 gap-8">
          {!isAuthenticated && (
            <div className="w-full max-w-sm rounded-xl border border-amber-500/30 bg-amber-500/10 p-4 text-center">
              <h3 className="text-sm font-semibold text-amber-500">Learning offline</h3>
              <p className="mt-1 text-xs text-muted-foreground">
                {isOnline
                  ? 'Your session is not connected. Cached lessons remain available.'
                  : 'You are offline. Cached lessons and ebooks remain available; login will be available when you reconnect.'}
              </p>
              {isOnline && (
                <Link
                  href="/auth/login?next=%2Fclasses"
                  className="mt-3 inline-flex rounded-lg bg-primary px-4 py-2 text-xs font-semibold text-primary-foreground hover:bg-primary/90"
                >
                  Log in to sync progress
                </Link>
              )}
            </div>
          )}

          <div className="flex flex-col items-center gap-2">
            <span className="text-xs font-bold text-muted-foreground uppercase tracking-widest">Name</span>
            <ProfileDisplay />
          </div>
          
          <div className="flex flex-col items-center gap-2">
            <span className="text-xs font-bold text-muted-foreground uppercase tracking-widest">Stats</span>
            <PointsBox dashboardState={dashboardState} isLoading={isDashboardLoading} />
          </div>

          {/* Unified Character Progression Modal Launcher */}
          <div className="flex flex-col items-center gap-3 w-full max-w-sm mt-4">
            <span className="text-xs font-bold text-muted-foreground uppercase tracking-widest">Progression</span>
            <button
              onClick={() => setShowProgressionModal(true)}
              className="w-full flex items-center justify-between p-4 rounded-xl border border-primary/30 bg-primary/5 hover:bg-primary/10 transition-colors group shadow-sm"
            >
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-primary/20 flex items-center justify-center border border-primary/30">
                  <Target className="w-5 h-5 text-primary" />
                </div>
                <div className="text-left">
                  <h4 className="font-bold text-sm text-foreground uppercase tracking-wider group-hover:text-primary transition-colors">Skill Tree & Roles</h4>
                  <p className="text-xs text-muted-foreground">View your character progression</p>
                </div>
              </div>
              <ChevronRight className="w-5 h-5 text-muted-foreground group-hover:text-primary transition-colors" />
            </button>
          </div>

          {isFounder && (
            <div className="flex flex-col items-center gap-3">
              <span className="text-xs font-bold text-muted-foreground uppercase tracking-widest">Founder Test Controls</span>
              <div className="flex flex-wrap items-center justify-center gap-3">
                <button
                  type="button"
                  onClick={handleFounderTierBypassToggle}
                  disabled={isDashboardMutating}
                  className={`px-4 py-2 rounded-full text-sm font-semibold transition-colors border ${
                    dashboardState?.founderBypassActive
                      ? 'bg-amber-500 text-black border-amber-500'
                      : 'bg-background text-foreground border-border hover:border-amber-500/50'
                  }`}
                >
                  {dashboardState?.founderBypassActive ? 'Bypass Enabled' : 'Unlock All Tiers'}
                </button>
                <button
                  type="button"
                  onClick={handleFounderReset}
                  disabled={isDashboardMutating}
                  className="px-4 py-2 rounded-full text-sm font-semibold transition-colors border border-orange-500/50 bg-orange-500/10 text-orange-600 dark:text-orange-400 hover:bg-orange-500/20"
                >
                  Reset Lesson MP / Progress
                </button>
              </div>
            </div>
          )}

          <div className="flex flex-col items-center gap-2">
            <span className="text-xs font-bold text-muted-foreground uppercase tracking-widest">Typography</span>
            <Select value={fontFamily} onValueChange={setFontFamily}>
              <SelectTrigger className="w-[180px]">
                <Type className="w-4 h-4 mr-2 text-muted-foreground" />
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="inter">Inter (Sans)</SelectItem>
                <SelectItem value="merriweather">Merriweather (Serif)</SelectItem>
                <SelectItem value="jetbrains-mono">JetBrains Mono</SelectItem>
                <SelectItem value="playfair">Playfair Display</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="flex flex-col items-center gap-2">
            <span className="text-xs font-bold text-muted-foreground uppercase tracking-widest">Global Settings</span>
            <div className="flex flex-col gap-3 w-[240px]">
              <div className="flex items-center justify-between bg-background border border-border/50 px-3 py-2 rounded-xl shadow-sm">
                <span className="text-xs font-medium">Auto-Cache Opened Lessons</span>
                <Switch checked={autoCacheEnabled} onCheckedChange={setAutoCacheEnabled} />
              </div>
              <div className="flex items-center justify-between bg-background border border-border/50 px-3 py-2 rounded-xl shadow-sm">
                <span className="text-xs font-medium">Show Feedback Chat</span>
                <Switch checked={showChatAgent} onCheckedChange={handleChatAgentToggle} />
              </div>
            </div>
          </div>

          <div className="flex flex-col items-center gap-2 mt-4">
            <span className="text-xs font-bold text-red-500/80 uppercase tracking-widest">Danger Zone</span>
            {!confirmClearCache ? (
              <button
                onClick={() => setConfirmClearCache(true)}
                className="w-[240px] flex items-center justify-center gap-2 py-2 rounded-xl border border-red-500/30 bg-red-500/10 text-red-500 hover:bg-red-500/20 transition-colors text-sm font-semibold shadow-sm"
              >
                <Trash2 className="w-4 h-4" />
                Clear Offline Storage
              </button>
            ) : (
              <div className="w-[240px] flex flex-col items-center gap-2 bg-red-500/10 border border-red-500/50 p-3 rounded-xl shadow-sm">
                <span className="text-xs text-red-500 text-center font-medium">Are you sure? This will delete all downloaded lessons.</span>
                <div className="flex gap-2 w-full mt-1">
                  <button
                    onClick={() => setConfirmClearCache(false)}
                    className="flex-1 py-1.5 rounded-lg border border-border bg-background hover:bg-muted text-xs font-semibold"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleClearCache}
                    className="flex-1 py-1.5 rounded-lg bg-red-500 hover:bg-red-600 text-white text-xs font-semibold shadow-sm"
                  >
                    Yes, Clear
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      );
    }

    if (activeTab === 'lab') {
      return (
        <GamifyLabClient
          lessonTree={roleFilteredLessonTree}
          fontFamily={fontFamily}
          fontSizeLevel={fontSizeLevel}
          onFontSizeChange={setFontSizeLevel}
          fullWidth={fullWidth}
          onFullWidthToggle={() => setFullWidth(prev => !prev)}
        />
      );
    }

    if (activeTab === 'jungle-academy') {
      return <JungleAcademyClient />;
    }
  };

  const ProfileDisplay = () => {
    const safeName = userName || 'Unknown';
    return (
      <div className="flex items-center gap-3">
        <div className="flex flex-col min-w-0">
          <span className="text-sm font-bold leading-tight truncate uppercase tracking-widest text-primary">{safeName}</span>
        </div>
      </div>
    );
  };

  const PointsBox = ({ dashboardState, isLoading }: { dashboardState: any; isLoading: boolean }) => (
    <div className="flex items-center justify-center gap-2 bg-zinc-100 dark:bg-zinc-900/50 p-2 rounded-xl border border-border/50">
      <Tooltip>
        <TooltipTrigger asChild>
          <div className="flex items-center gap-1.5 bg-background px-2 py-1.5 rounded-lg cursor-help hover:bg-muted transition-colors border border-border/50 shadow-sm">
            <Target className="w-3.5 h-3.5 text-blue-500" />
            <span className="text-xs font-bold font-mono">{isLoading ? '...' : '0'}</span>
          </div>
        </TooltipTrigger>
        <TooltipContent><p>ELI Rating</p></TooltipContent>
      </Tooltip>

      <Tooltip>
        <TooltipTrigger asChild>
          <div className="flex items-center gap-1.5 bg-background px-2 py-1.5 rounded-lg cursor-help hover:bg-muted transition-colors border border-border/50 shadow-sm">
            <Zap className="w-3.5 h-3.5 text-amber-500" />
            <span className="text-xs font-bold font-mono">{isLoading ? '...' : (dashboardState?.profile?.mpBalance ?? 0)}</span>
          </div>
        </TooltipTrigger>
        <TooltipContent><p>Micro Points (MP)</p></TooltipContent>
      </Tooltip>

      <Tooltip>
        <TooltipTrigger asChild>
          <div className="flex items-center gap-1.5 bg-background px-2 py-1.5 rounded-lg cursor-help hover:bg-muted transition-colors border border-border/50 shadow-sm">
            <Brain className="w-3.5 h-3.5 text-purple-500" />
            <span className="text-xs font-bold font-mono">{isLoading ? '...' : (dashboardState?.profile?.cp ?? 0)}</span>
          </div>
        </TooltipTrigger>
        <TooltipContent><p>Character Points (CP)</p></TooltipContent>
      </Tooltip>
    </div>
  );

  const NavigationItems = ({ compact = false }: { compact?: boolean }) => {
    const baseItemClasses = compact
      ? 'w-full min-h-[56px] flex-col justify-center gap-1 px-1.5 py-2'
      : `${isDesktopLayout ? 'md:w-full flex-row gap-3 md:justify-start md:px-4 md:py-3' : 'w-10 flex-col gap-0.5 justify-center px-1 py-1'} p-2`;

    const indicatorClasses = compact
      ? 'absolute left-0.5 top-1/2 h-6 w-0.5 -translate-y-1/2 rounded-full'
      : `absolute top-0 ${isDesktopLayout ? 'md:left-0 md:top-auto md:inset-y-2 md:inset-x-auto md:w-1 md:h-auto' : 'inset-x-3 h-0.5'}`;

    const iconClasses = compact ? 'w-4 h-4 shrink-0' : `w-4 h-4 shrink-0 ${isDesktopLayout ? 'md:w-4 md:h-4' : ''}`;
    const labelClasses = compact ? 'text-[8px] leading-tight font-medium uppercase tracking-[0.16em]' : `text-[8px] leading-none ${isDesktopLayout ? 'md:text-sm' : ''} font-medium`;

    const renderItem = (
      tab: TabState,
      label: string,
      Icon: React.ComponentType<{ className?: string }>,
      activeColor: string = 'primary'
    ) => {
      const isActive = activeTab === tab;
      const activeClasses = activeColor === 'amber'
        ? (compact ? 'text-amber-500 bg-amber-500/10 border-amber-500/30' : `text-amber-500 ${isDesktopLayout ? 'md:bg-amber-500/10' : ''}`)
        : (compact ? 'text-primary bg-primary/10 border-primary/30' : `text-primary ${isDesktopLayout ? 'md:bg-primary/10' : ''}`);
      const idleClasses = compact
        ? 'text-muted-foreground border-transparent hover:text-foreground hover:bg-muted/50 hover:border-border/60'
        : `text-muted-foreground hover:text-foreground ${isDesktopLayout ? 'md:hover:bg-muted/50' : ''}`;
      const indicatorColor = activeColor === 'amber' ? 'bg-amber-500 shadow-[0_0_10px_rgba(245,158,11,0.5)]' : 'bg-primary shadow-[0_0_10px_rgba(var(--primary),0.5)]';

      return (
        <button
          type="button"
          key={tab}
          onClick={() => setActiveTab(tab)}
          className={`flex items-center transition-colors relative group rounded-xl border ${baseItemClasses} ${isActive ? activeClasses : idleClasses}`}
        >
          {isActive && <div className={`${indicatorClasses} ${indicatorColor}`} />}
          <Icon className={`${iconClasses} ${!isActive ? 'group-hover:scale-110 transition-transform' : ''}`} />
          <span className={labelClasses}>{label}</span>
        </button>
      );
    };

    return (
      <>
        {renderItem('lessons', 'Lessons', BookOpen)}
        {renderItem('map', 'Map', MapIcon)}
        {renderItem('challenges', 'Challenges', Swords)}
        {renderItem('ebooks', 'Ebooks', Library)}
        {renderItem('dash', 'Dash', LayoutDashboard)}
        {isFounder && isLocalhost && renderItem('lab', 'Lab', Beaker, 'amber')}
        {isFounder && renderItem('jungle-academy', 'Academy', Trophy)}
      </>
    );
  };

  const renderLessonsBrowserHeader = () => (
    <LearningAppHeader
      title="Lessons"
      center={(
        <LearningAppSegmentTabs
          items={lessonAreaItems}
          activeId={activeLessonAreaId}
          onSelect={setActiveLessonAreaId}
        />
      )}
      actions={(
        <div className="flex min-w-0 items-center gap-1.5">
          <div className="flex min-w-0 items-center gap-1 overflow-x-auto no-scrollbar">
            <button
              type="button"
              onClick={() => setActiveFilter('not-started')}
              className={`shrink-0 rounded-full px-2 py-1 text-[10px] font-medium transition-colors ${
                activeFilter === 'not-started'
                  ? 'bg-primary text-primary-foreground shadow-sm'
                  : 'bg-muted text-muted-foreground hover:bg-muted/80'
              }`}
            >
              Not Started
            </button>
            <button
              type="button"
              onClick={() => setActiveFilter('in-progress')}
              className={`shrink-0 rounded-full px-2 py-1 text-[10px] font-medium transition-colors ${
                activeFilter === 'in-progress'
                  ? 'bg-primary text-primary-foreground shadow-sm'
                  : 'bg-muted text-muted-foreground hover:bg-muted/80'
              }`}
            >
              In Progress
            </button>
            <button
              type="button"
              onClick={() => setActiveFilter('completed')}
              className={`shrink-0 rounded-full px-2 py-1 text-[10px] font-medium transition-colors ${
                activeFilter === 'completed'
                  ? 'bg-primary text-primary-foreground shadow-sm'
                  : 'bg-muted text-muted-foreground hover:bg-muted/80'
              }`}
            >
              Completed
            </button>
          </div>

          <div className="flex shrink-0 items-center gap-1">
            <RoleFilterModal
              userRoles={effectiveUserRoles}
              visibleRoles={visibleRoles}
              setVisibleRoles={setVisibleRoles}
              showLockedRoles={showLockedRoles}
              setShowLockedRoles={setShowLockedRoles}
              showBaseLessons={showBaseLessons}
              setShowBaseLessons={setShowBaseLessons}
              triggerClassName="w-7 px-0"
              showLabel={false}
            />


            <LearningAppHeaderButton
              onClick={handleRefresh}
              title="Sync library tree from Database"
              className="w-7 px-0"
            >
              <RefreshCw className={`w-3.5 h-3.5 ${isRefreshing ? 'animate-spin text-primary' : ''}`} />
            </LearningAppHeaderButton>
          </div>
        </div>
      )}
    />
  );

  return (
    <TooltipProvider>
      <div className={`relative bg-background overflow-hidden flex transition-all duration-300 ease-in-out ${getContainerClasses()}`}>
        
        {/* --- 2-SECOND SPLASH SCREEN --- */}
        <AnimatePresence>
          {showSplash && (
            <motion.div
              initial={{ opacity: 1 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 0.5, ease: "easeInOut" }}
              className="absolute inset-0 z-50 flex flex-col items-center justify-center bg-zinc-950 text-white"
            >
              <motion.div
                initial={{ scale: 0.8, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                transition={{ duration: 0.8, ease: "easeOut" }}
                className="flex flex-col items-center gap-4"
              >
                <div className="w-24 h-24 rounded-full bg-primary/20 flex items-center justify-center border border-primary/50 shadow-[0_0_40px_rgba(var(--primary),0.3)]">
                  <Brain className="w-12 h-12 text-primary" />
                </div>
                <h1 className="text-2xl font-light tracking-widest uppercase">Digital Universe</h1>
                <p className="text-zinc-500 text-sm tracking-widest">LEARNING GAMES</p>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* --- DESKTOP SIDEBAR --- */}
        <aside className={`flex-col w-20 h-full border-r border-border/50 bg-background/85 backdrop-blur-md shrink-0 ${(!isDesktopLayout || fullWidth) ? 'hidden' : 'hidden md:flex'}`}>
          <nav className="flex-1 overflow-y-auto p-2 flex flex-col items-center gap-1.5">
            <NavigationItems compact />
          </nav>
          <div className="p-2 border-t border-border/50">
            {isPWA ? (
              <button
                type="button"
                onClick={() => window.close()}
                className="flex w-full min-h-[56px] flex-col items-center justify-center gap-1 rounded-xl border border-border/60 bg-muted/30 px-1.5 py-2 text-[8px] font-medium uppercase tracking-[0.16em] text-muted-foreground transition-colors hover:border-primary/40 hover:bg-muted/60 hover:text-foreground"
              >
                <ArrowLeft className="w-3.5 h-3.5 shrink-0" />
                <span className="text-center leading-tight">Close App</span>
              </button>
            ) : (
              <Link
                href="/"
                className="flex w-full min-h-[56px] flex-col items-center justify-center gap-1 rounded-xl border border-border/60 bg-muted/30 px-1.5 py-2 text-[8px] font-medium uppercase tracking-[0.16em] text-muted-foreground transition-colors hover:border-primary/40 hover:bg-muted/60 hover:text-foreground"
              >
                <ArrowLeft className="w-3.5 h-3.5 shrink-0" />
                <span className="text-center leading-tight">Exit App</span>
              </Link>
            )}
          </div>
        </aside>

        {/* --- MAIN CONTENT AREA --- */}
        <main className="flex-1 flex flex-col min-w-0 h-full overflow-hidden relative">

          {!selectedFile && activeTab === 'lessons' && (
            <div className="shrink-0">
              {renderLessonsBrowserHeader()}
            </div>
          )}

          {/* NEXT LESSON CARD - Independent from Tree Scroll */}
          {activeTab === 'lessons' && focusedDir && !selectedFile && (
            <div className="w-full bg-background/95 backdrop-blur-md border-b border-border/30 z-30 shadow-sm relative px-4 md:px-8 py-4">
               <div className="max-w-4xl mx-auto w-full">
                 <NextLessonCard 
                   gamificationState={gamificationState}
                   focusedNode={focusedDir}
                   onNodeClick={handleFileClick}
                 />
               </div>
            </div>
          )}

          <ScrollArea className="flex-1 w-full px-4 pb-3 md:px-8 md:pb-8 md:pt-3">
            <div className="pb-24 max-w-4xl mx-auto w-full">
              {renderContent()}
            </div>
          </ScrollArea>
        </main>

        {/* --- MOBILE STICKY BOTTOM TAB BAR --- */}
        <nav className={`border-t border-border/50 bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/80 shrink-0 sticky bottom-0 z-50 ${fullWidth ? 'hidden' : !isDesktopLayout ? 'block' : 'md:hidden'}`}>
          <div className="flex justify-between items-center h-12 w-full px-0.5">
            <NavigationItems />
            {isPWA ? (
              <button
                type="button"
                onClick={() => window.close()}
                className="flex w-9 flex-col items-center justify-center gap-0.5 rounded-lg p-1 text-[8px] font-medium text-muted-foreground transition-colors hover:text-foreground"
              >
                <ArrowLeft className="w-3.5 h-3.5 shrink-0" />
                <span>Close</span>
              </button>
            ) : (
              <Link
                href="/"
                className="flex w-9 flex-col items-center justify-center gap-0.5 rounded-lg p-1 text-[8px] font-medium text-muted-foreground transition-colors hover:text-foreground"
              >
                <ArrowLeft className="w-3.5 h-3.5 shrink-0" />
                <span>Exit</span>
              </Link>
            )}
          </div>
        </nav>

      </div>
      
      <CharacterProgressionModal
        open={showProgressionModal}
        onOpenChange={setShowProgressionModal}
        character={character}
        globalMp={dashboardState?.profile?.mpBalance || 0}
      />
    </TooltipProvider>
  );
}
